import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'

// Mitglieder-Karten store — mirrors src/stores/supabaseEquipmentStore.ts.
// Cards back the public /pass/:serial page (branded card + QR + Google Wallet).
// Admin-only writes (also enforced server-side by RLS: authenticated insert/update/delete).

export interface Card {
  id: string
  serial: string
  holder_name: string
  season: string | null
  email: string | null
  status: 'active' | 'revoked'
  created_at: string
  foxi_id: string | null
  foxi_url: string | null
  sent_at: string | null
}

interface CardsStore {
  cards: Card[]
  loading: boolean
  error: string | null
  fetchCards: () => Promise<void>
  addCard: (holder_name: string, season: string, email: string) => Promise<{ error?: string; serial?: string; id?: string }>
  updateCard: (id: string, fields: { holder_name: string; season: string; email: string }) => Promise<{ error?: string }>
  revokeCard: (id: string) => Promise<{ error?: string }>
  deleteCard: (id: string) => Promise<{ error?: string }>
  /** Create the FOXi wallet ticket on Passcreator (optionally email it to the member). */
  createFoxi: (id: string, sendEmail: boolean) => Promise<{ error?: string; foxiUrl?: string; emailed?: boolean; guardBlocked?: boolean }>
  /** Passcreator bulk CSV (name;email;ticket_id;season) of all active cards. */
  exportCsv: () => void
}

// FCF- + 24 hex chars (matches the Azure app's serial style).
function newSerial(): string {
  const hex = crypto.randomUUID().replace(/-/g, '').slice(0, 24).toUpperCase()
  return `FCF-${hex}`
}

const requireAdmin = (): string | null => {
  const { profile } = useAuthStore.getState()
  return !profile || profile.role !== 'admin' ? 'Nur Administratoren können Karten verwalten' : null
}

export const useCardsStore = create<CardsStore>((set, get) => ({
  cards: [],
  loading: false,
  error: null,

  fetchCards: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      set({ cards: (data as Card[]) || [], loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  addCard: async (holder_name, season, email) => {
    const denied = requireAdmin()
    if (denied) return { error: denied }
    const name = holder_name.trim()
    if (!name) return { error: 'Name fehlt' }
    try {
      const serial = newSerial()
      const { data, error } = await supabase
        .from('cards')
        .insert({ serial, holder_name: name, season: season.trim() || null, email: email.trim() || null, status: 'active' })
        .select('id')
        .single()
      if (error) throw error
      await get().fetchCards()
      return { serial, id: (data as { id: string }).id }
    } catch (error: any) {
      return { error: error.message }
    }
  },

  updateCard: async (id, fields) => {
    const denied = requireAdmin()
    if (denied) return { error: denied }
    const name = fields.holder_name.trim()
    if (!name) return { error: 'Name fehlt' }
    try {
      const { error } = await supabase
        .from('cards')
        .update({ holder_name: name, season: fields.season.trim() || null, email: fields.email.trim() || null })
        .eq('id', id)
      if (error) throw error
      await get().fetchCards()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  revokeCard: async (id) => {
    const denied = requireAdmin()
    if (denied) return { error: denied }
    try {
      const { error } = await supabase.from('cards').update({ status: 'revoked' }).eq('id', id)
      if (error) throw error
      await get().fetchCards()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteCard: async (id) => {
    const denied = requireAdmin()
    if (denied) return { error: denied }
    try {
      const { error } = await supabase.from('cards').delete().eq('id', id)
      if (error) throw error
      await get().fetchCards()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  createFoxi: async (id, sendEmail) => {
    const denied = requireAdmin()
    if (denied) return { error: denied }
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) return { error: 'Nicht angemeldet.' }
      const res = await fetch('/.netlify/functions/foxi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cardId: id, sendEmail }),
      })
      const j = (await res.json().catch(() => ({}))) as { error?: string; foxiUrl?: string; emailed?: boolean; guardBlocked?: boolean }
      if (!res.ok) return { error: j.error || `Fehler (${res.status})` }
      await get().fetchCards()
      return { foxiUrl: j.foxiUrl, emailed: j.emailed, guardBlocked: j.guardBlocked }
    } catch (error: any) {
      return { error: error.message }
    }
  },

  exportCsv: () => {
    // Passcreator Massenverarbeitung format: semicolon separator, quoted values.
    const esc = (v: string | null) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const rows = get()
      .cards.filter((c) => c.status === 'active')
      .map((c) => [esc(c.holder_name), esc(c.email), esc(c.serial), esc(c.season)].join(';'))
    const csv = ['name;email;ticket_id;season', ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `foxi-karten-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  },
}))
